import { useState, useCallback, useRef, useEffect } from 'react'
import _ from 'lodash'

const defaultValidation = {
  uploadDocument: false,
  voiceover: false,
  videoStyle: false,
  shotList: false,
  imageGeneration: false,
  videoGeneration: false
}

/**
 * Custom hook to manage wizard step navigation and validation
 * Handles step transitions, URL syncing, and validation state
 */
export const useStepNavigation = ({
  videoId,
  initialStep,
  steps,
  isFetchingVideoData
} = {}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [stepsValidation, setStepsValidation] = useState(defaultValidation)
  const initialStepHandled = useRef(false)

  /**
   * Get step index from step key
   */
  const getStepIndexByKey = useCallback(
    stepKey => {
      if (!stepKey || !steps) return 0
      const index = steps.findIndex(s => s.key === stepKey)
      return index !== -1 ? index : 0
    },
    [steps]
  )

  /**
   * Update URL with current step
   */
  const updateUrlWithStep = useCallback(
    (stepIndex, id = videoId) => {
      if (!id || !steps) return
      const stepKey = steps[stepIndex]?.key || 'uploadDocument'
      window.history.replaceState(null, '', `/video-create/${id}/${stepKey}`)
    },
    [videoId, steps]
  )

  /**
   * Handle initial step navigation based on URL
   */
  useEffect(() => {
    if (
      !isFetchingVideoData &&
      videoId &&
      initialStep &&
      !initialStepHandled.current
    ) {
      const isValidationLoaded = !_.isEqual(stepsValidation, defaultValidation)

      if (isValidationLoaded && steps) {
        const stepIndex = getStepIndexByKey(initialStep)
        let targetStep = stepIndex

        for (let i = 0; i < stepIndex; i++) {
          if (!stepsValidation[steps[i]?.key]) {
            targetStep = i
            break
          }
        }

        setCurrentStep(targetStep)
        updateUrlWithStep(targetStep)
        initialStepHandled.current = true
      }
    }
  }, [
    isFetchingVideoData,
    videoId,
    initialStep,
    stepsValidation,
    steps,
    getStepIndexByKey,
    updateUrlWithStep
  ])

  /**
   * Navigate to next step
   */
  const handleNext = useCallback(() => {
    if (!steps) return
    const nextStep = Math.min(currentStep + 1, steps.length - 1)
    setCurrentStep(nextStep)
    updateUrlWithStep(nextStep)
  }, [currentStep, steps, updateUrlWithStep])

  /**
   * Navigate to previous step
   */
  const handlePrevious = useCallback(() => {
    const prevStep = Math.max(currentStep - 1, 0)
    setCurrentStep(prevStep)
    updateUrlWithStep(prevStep)
  }, [currentStep, updateUrlWithStep])

  /**
   * Navigate to specific step by key
   */
  const navigateToStep = useCallback(
    stepKey => {
      if (!steps) return
      const stepIndex = steps.findIndex(s => s.key === stepKey)
      if (stepIndex !== -1) {
        setCurrentStep(stepIndex)
        updateUrlWithStep(stepIndex)
      }
    },
    [steps, updateUrlWithStep]
  )

  /**
   * Update step validation
   */
  const updateStepValidation = useCallback((key, isValid) => {
    setStepsValidation(prev => {
      if (prev[key] === isValid) return prev
      return {
        ...prev,
        [key]: isValid
      }
    })
  }, [])

  /**
   * Get previous and next step titles
   */
  const getPreviousNextStepTitles = useCallback(() => {
    if (!steps) return { previousStepTitle: '', nextStepTitle: '' }

    const previousStepIndex = Math.max(currentStep - 1, 0)
    const nextStepIndex = Math.min(currentStep + 1, steps.length - 1)

    return {
      previousStepTitle: steps[previousStepIndex]?.title || '',
      nextStepTitle: steps[nextStepIndex]?.title || ''
    }
  }, [currentStep, steps])

  /**
   * Determine if next button should be shown
   */
  const shouldShowNextButton = useCallback(() => {
    if (!steps) return false
    // Don't show on last step
    if (currentStep === steps.length - 1) return false
    return true
  }, [currentStep, steps])

  /**
   * Check if all scenes have been generated
   */
  const isAllScenesGenerated = useCallback(shotList => {
    if (!shotList || shotList.length === 0) return false
    return shotList.every(scene =>
      scene.shots.every(shot => shot.vidSrc || (shot.is_static && shot.imageSrc))
    )
  }, [])

  /**
   * Reset step navigation state
   */
  const resetNavigation = useCallback(() => {
    setCurrentStep(0)
    setStepsValidation(defaultValidation)
    initialStepHandled.current = false
  }, [])

  return {
    // State
    currentStep,
    stepsValidation,

    // Actions
    handleNext,
    handlePrevious,
    navigateToStep,
    updateStepValidation,
    setStepsValidation,
    resetNavigation,

    // Utilities
    getStepIndexByKey,
    updateUrlWithStep,
    getPreviousNextStepTitles,
    shouldShowNextButton,
    isAllScenesGenerated,

    // Constants
    defaultValidation
  }
}

export default useStepNavigation
